"use client";

import { shouldUseAnchorForLink } from "@faire/web--source/ui/lib";
import { Anchor, Link } from "@faire/web--source/ui/LinkAnchor";
import { IAction } from "@faire/web-api--source/indigofair/data/IAction";
import React, { useMemo } from "react";

import { extractLinkFromActions } from "@faire/retailer/components/SDUI/__internal__/ActionHandler/extractLinkFromActions";
import { useSDUIOnClickHandler } from "@faire/retailer/components/SDUI/__internal__/ActionHandler/useSDUIOnClickHandler";
import {
  ComponentLoggingEventParams,
  LoggingEventParams,
} from "@faire/retailer/components/SDUI/__internal__/types";
import {
  useCategoryNavigationPrefetch,
  isCategoryUrl,
} from "@faire/retailer/lib/useCategoryNavigationPrefetch";

export const SDUIOnClickHandler = ({
  children,
  className,
  componentStyle,
  onClickActions,
  loggingData,
  logSDUIClickEvent = true,
  logLegacyClickEvent = false,
}: {
  children: React.ReactNode;
  className?: string;
  componentStyle?: React.CSSProperties;
  onClickActions: IAction[];
  loggingData: LoggingEventParams | undefined;
  logSDUIClickEvent?: boolean;
  logLegacyClickEvent?: boolean;
}) => {
  const { link, nonNavigationActions } = useMemo(
    () => extractLinkFromActions(onClickActions ?? []),
    [onClickActions]
  );
  const {
    prefetchOnMouseEnter,
    prefetchOnTouchStart,
    clearPrefetchTimeoutOnBlur,
  } = useCategoryNavigationPrefetch();

  const onClickHandler = useSDUIOnClickHandler(
    nonNavigationActions,
    loggingData ?? ComponentLoggingEventParams.build({}),
    { logSDUIClickEvent, logLegacyClickEvent }
  );

  if (link !== undefined) {
    if (shouldUseAnchorForLink(link)) {
      return (
        <Anchor
          className={className}
          style={componentStyle}
          href={link}
          onClick={onClickHandler}
        >
          {children}
        </Anchor>
      );
    } else {
      const shouldEnableNextPrefetch = isCategoryUrl(link);
      return (
        <Link
          className={className}
          style={componentStyle}
          to={link}
          onClick={onClickHandler}
          onMouseEnter={
            shouldEnableNextPrefetch
              ? () => prefetchOnMouseEnter(link)
              : undefined
          }
          onTouchStart={
            shouldEnableNextPrefetch
              ? () => prefetchOnTouchStart(link)
              : undefined
          }
          onMouseLeave={
            shouldEnableNextPrefetch ? clearPrefetchTimeoutOnBlur : undefined
          }
        >
          {children}
        </Link>
      );
    }
  }
  return (
    <div className={className} onClick={onClickHandler} style={componentStyle}>
      {children}
    </div>
  );
};
